import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import _get from 'lodash/get';
import styles from './index.module.scss';

import { If } from 'utils';
import { UiKit } from 'components';
import { Highlight } from 'components/ReactTable';
import { TableRow, TableActionsDropdown } from 'components/Tables';
import { RequestAction } from 'views/Requests/components';
import LineItemsTableRows from '../LineItemsTableRows';
import RequestStatus from '../../RequestStatus';
import { useRetexes } from 'views/Requests/hooks';
import { hasRequestTerminated, formatPrice } from 'views/Requests/methods';
import { getPaymentType, isOsPendingPayment } from 'utils/methods';
import { ROUTES } from 'views/Requests/stubs';
import { ReactComponent as ArrowIcon } from 'assets/img/icons/down-only.svg';
import { ReactComponent as NoteIcon } from 'assets/img/icons/note.svg';

export default function RequestsTableRow({
  data = {},
  hideCTA = false,
  hideArrow = false,
  isExpanded = false,
  isDisabled = false,
  onExpand = () => {},
  onDisable = () => {},
  renderRetexTitle = () => null,
  generateRowData = () => null,
  generateRetexRowData = () => null,
  onBeforeProcessing = () => {},
  onAfterProcessing = () => {},
  onOpenCancelHoldModal = () => {}
}) {
  const [processingId, setProcessingId] = useState(null);
  const { retexes, isFetching, onUpdateRetex } = useRetexes(data, isExpanded);

  const id = _get(data, 'id', '');
  const osName = _get(data, 'name', '');
  const orderName = _get(data, 'order_name', '');
  const status = _get(data, 'status', '');
  const amount = _get(data, 'amount', 0);
  const currency = _get(data, 'currency', '');
  const isOnHold = _get(data, 'on_hold', false);
  const note = _get(data, 'last_retailer_note', '');
  const isPendingPayment = isOsPendingPayment(status);
  const isRequestTerminated = hasRequestTerminated(status);

  const isLocked = isOnHold && !isRequestTerminated;
  const paymentType = getPaymentType(amount);
  const formattedPrice = formatPrice(Math.abs(amount), currency);
  const requestDetailsUrl = ROUTES.REQUEST_DETAILS.replace(':osId', id);

  const onPostApproval = useCallback(() => {
    setProcessingId(null);
    onDisable(false);
    onAfterProcessing(true);
  }, [onDisable, onAfterProcessing]);

  const DEFAULT_ROW_DATA = [
    {
      lg: 3,
      customClass: styles.leftOffset,
      CustomCell: {
        title: (
          <div className="d-flex align-items-center">
            <Link to={{ pathname: requestDetailsUrl, state: { osName } }}>
              <Highlight data={data} attribute="name" className="link-text" />
            </Link>
            <If test={!!note}>
              <UiKit.Tooltip>
                <UiKit.Tooltip.Trigger className="ml-2">
                  <NoteIcon className={styles.note} />
                </UiKit.Tooltip.Trigger>
                <UiKit.Tooltip.Content innerClassName="text-left">
                  {note}
                </UiKit.Tooltip.Content>
              </UiKit.Tooltip>
            </If>
          </div>
        ),
        subTitle: orderName
      }
    },
    {
      lg: 2,
      CustomCell: {
        title: (
          <Highlight
            data={data}
            attribute="customer_name"
            className="text-capitalize"
          />
        ),
        subTitle: <Highlight data={data} attribute="customer_email" />
      }
    },
    {
      lg: 2,
      customClass: styles.amount,
      CustomCell: {
        title: formattedPrice,
        subTitle: paymentType
      }
    },
    {
      lg: 3,
      customClass: 'light-text',
      title: <RequestStatus status={status} isLocked={isLocked} />
    }
  ];

  const actions = [];
  const disableMenu = isRequestTerminated || isPendingPayment;
  const values = generateRowData(data, isExpanded) || DEFAULT_ROW_DATA;

  if (!disableMenu) {
    if (!isLocked) {
      actions.push({
        title: 'Lock Request',
        onClick: () => {
          onOpenCancelHoldModal({
            type: 'hold',
            orderServiceID: id,
            extra: { holdVal: true }
          });
        }
      });
    }
    actions.push({
      title: 'Cancel Request',
      onClick: () => {
        onOpenCancelHoldModal({
          type: 'cancel',
          orderServiceID: id,
          orderServiceStatus: status
        });
      }
    });
  }

  values.push({
    lg: hideCTA ? 1 : 2,
    CustomComponent: (
      <div className={styles.actions}>
        {!hideCTA && (
          <RequestAction
            isCompact
            data={data}
            className="w-100"
            retexes={retexes}
            isDisabled={isDisabled}
            isRefreshing={isFetching}
            onBeforeProcessing={onBeforeProcessing}
            onSetProcessingId={setProcessingId}
            onRetexUpdate={onUpdateRetex}
            onAfterProcessing={onPostApproval}
          />
        )}
        <TableActionsDropdown
          items={actions}
          isDisabled={disableMenu}
          onClick={e => e.stopPropagation()}
          className="ml-auto"
        />
      </div>
    )
  });

  return (
    <div
      className={cn(styles.container, {
        [styles.locked]: isLocked,
        [styles.expanded]: isExpanded
      })}
    >
      {!hideArrow && (
        <ArrowIcon
          className={cn(styles.arrowIcon, { [styles.active]: isExpanded })}
        />
      )}
      <TableRow
        key={id}
        values={values}
        onClick={() => onExpand(id)}
        customTableRowWrapClass={styles.osRowWrapper}
        customTableRowClass={cn(styles.osRow, {
          [styles.disabled]: isDisabled
        })}
      />
      <LineItemsTableRows
        isOpen={isExpanded}
        osId={id}
        osName={osName}
        retexes={retexes}
        isFetching={isFetching}
        processingId={processingId}
        onDisable={onDisable}
        onUpdateRetex={onUpdateRetex}
        isDisabled={isDisabled || isOnHold}
        generateRowData={generateRetexRowData}
        renderRetexTitle={renderRetexTitle}
        onAfterProcessing={onAfterProcessing}
      />
    </div>
  );
}
